import { numberToHex } from 'viem'
import { switchChain } from 'wagmi/actions'
import type { Connector } from 'wagmi'

import { getActiveChainId } from '../../config/active'
import { tMsg } from '@/features/locale/translate'
import { infiniteCreativeChain, wagmiConfig } from '../../wagmi/config'
import { txSubmitDebug, txSubmitWarn } from '../../utils/debugTxSubmit'

type EIP1193Provider = {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>
}

type WalletAddChainParams = {
  chainId: `0x${string}`
  chainName: string
  nativeCurrency: { name: string; symbol: string; decimals: number }
  rpcUrls: string[]
  blockExplorerUrls?: string[]
}

function getErrorCode(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = Number((error as { code: unknown }).code)
    return Number.isFinite(code) ? code : undefined
  }
  return undefined
}

/** Parámetros de `wallet_addEthereumChain` para la cadena activa del build. */
export function buildWalletAddChainParams(chainId = getActiveChainId()): WalletAddChainParams {
  const explorerUrl = infiniteCreativeChain.blockExplorers?.default?.url
  return {
    chainId: numberToHex(chainId),
    chainName: infiniteCreativeChain.name,
    nativeCurrency: infiniteCreativeChain.nativeCurrency,
    rpcUrls: [...infiniteCreativeChain.rpcUrls.default.http],
    ...(explorerUrl ? { blockExplorerUrls: [explorerUrl] } : {}),
  }
}

async function getProvider(connector: Connector): Promise<EIP1193Provider> {
  try {
    return (await connector.getProvider({ chainId: getActiveChainId() })) as EIP1193Provider
  } catch {
    return (await connector.getProvider()) as EIP1193Provider
  }
}

async function addChainViaProvider(connector: Connector, chainId = getActiveChainId()): Promise<void> {
  const provider = await getProvider(connector)
  await provider.request({
    method: 'wallet_addEthereumChain',
    params: [buildWalletAddChainParams(chainId)],
  })
}

async function switchChainViaProvider(connector: Connector, chainId = getActiveChainId()): Promise<void> {
  const provider = await getProvider(connector)
  await provider.request({
    method: 'wallet_switchEthereumChain',
    params: [{ chainId: numberToHex(chainId) }],
  })
}

/**
 * Asegura que MetaMask tenga y use la cadena del build.
 * Los saldos de la app se leen por RPC propio; firmar exige la red en la wallet.
 */
export async function ensureAppChainInWallet(connector: Connector | undefined): Promise<void> {
  if (!connector) {
    throw new Error(tMsg().wallet.error.walletNotFound)
  }

  const chainId = getActiveChainId()
  const walletChainId = await connector.getChainId().catch(() => undefined)

  if (walletChainId === chainId) {
    txSubmitDebug('ensureAppChainInWallet: already on chain', { chainId })
    return
  }

  if (walletChainId === undefined) {
    await new Promise((resolve) => window.setTimeout(resolve, 400))
    const retryChainId = await connector.getChainId().catch(() => undefined)
    if (retryChainId === chainId) {
      txSubmitDebug('ensureAppChainInWallet: already on chain after retry', { chainId })
      return
    }
  }

  txSubmitDebug('ensureAppChainInWallet: configure chain', { walletChainId, chainId })

  const addEthereumChainParameter = {
    chainName: infiniteCreativeChain.name,
    nativeCurrency: infiniteCreativeChain.nativeCurrency,
    rpcUrls: [...infiniteCreativeChain.rpcUrls.default.http],
    ...(infiniteCreativeChain.blockExplorers?.default?.url
      ? { blockExplorerUrls: [infiniteCreativeChain.blockExplorers.default.url] }
      : {}),
  }

  try {
    await switchChain(wagmiConfig, {
      connector,
      chainId,
      addEthereumChainParameter,
    })
    return
  } catch (error) {
    txSubmitWarn('ensureAppChainInWallet: switchChain failed', { message: String(error) })
  }

  try {
    await switchChainViaProvider(connector, chainId)
    return
  } catch (switchError) {
    if (getErrorCode(switchError) !== 4902) {
      throw switchError
    }
  }

  await addChainViaProvider(connector, chainId)
  await switchChainViaProvider(connector, chainId)
}

export function mapEnsureChainError(error: unknown): Error {
  const message = error instanceof Error ? error.message : String(error)
  const lower = message.toLowerCase()
  const { wallet } = tMsg()

  if (lower.includes('user rejected') || lower.includes('user denied')) {
    return new Error(wallet.error.addChainRejected(infiniteCreativeChain.name))
  }

  return new Error(wallet.error.configureChainFailed)
}
